import { useEffect, useState, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Typography, Box, Paper, CardMedia } from "@mui/material";
import { getPublicInfo, useWindowSize } from "../../utils";
import SuspenseWrapper from "./SuspenseWrapper";
import type { Motto as MottoType } from "utils/types/Info.types";

interface Props {
  loading?: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.3 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 },
};

export const Motto: React.FC<Props> = ({ loading = false }) => {
  const [mottos, setMottos] = useState<MottoType[]>([]);
  const { isMobile, isPC } = useWindowSize();
  const [loadingMotto, setLoadingMotto] = useState(true);
  const controls = useAnimation();
  const { ref: mottoRef, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const fetchMottos = useCallback(async () => {
    setLoadingMotto(true);
    try {
      const res = await getPublicInfo<MottoType>(["mottos"], {
        page: 1,
        limit: 50,
        sortBy: "order",
        order: "asc",
      });


      const d: any = res?.data ?? null;
      let list: MottoType[] = [];

      // 1) { items, meta }
      if (d && typeof d === "object" && Array.isArray(d.items)) {
        list = d.items as MottoType[];
      }
      // 2) [items, meta]
      else if (Array.isArray(d) && Array.isArray(d[0])) {
        list = d[0] as MottoType[];
      }
      // 3) plain array
      else if (Array.isArray(d)) {
        list = d as MottoType[];
      }
      // 4) legacy map { id -> item }  OU {0: items[], 1: meta{}}
      else if (d && typeof d === "object") {
        const values = Object.values(d)
        if (values.length === 2 && Array.isArray(values[0]) && isPlainObject(values[1])) {
          list = values[0] as MottoType[];
        } else {
          list = values as MottoType[];
        }
      } else {
        console.warn("%c[Motto] ⚠️ Unrecognized format:", "color:#ff5722;font-weight:bold;", d);
      }

      // Tri de secours : par contenu HTML sans balises (ou par id si égalité)
      list = [...list].sort((a, b) => {
        const ta = stripHtml(a.motto ?? "");
        const tb = stripHtml(b.motto ?? "");
        const cmp = ta.localeCompare(tb);
        if (cmp !== 0) return cmp;
        return String(a.id ?? "").localeCompare(String(b.id ?? ""));
      });


      setMottos(list);
    } catch (e) {
      console.error("%c[Motto] ❌ Failed to load mottos:", "color:red;font-weight:bold;", e);
      setMottos([]);
    } finally {
      setLoadingMotto(false);
    }
  }, []);

  useEffect(() => { fetchMottos(); }, [fetchMottos]);
  useEffect(() => { if (inView) controls.start("visible"); }, [controls, inView]);

  return (
    <Box
      sx={{
        padding: "16px",
        margin: "16px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <motion.div
        ref={mottoRef}
        className="flex w-full items-center justify-between flex-col"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {mottos.map((motto, index) => (
          <motion.div
            key={motto.id ?? `motto-${index}`}
            className="flex gap-4 items-center justify-center flex-row w-full"
            variants={itemVariants}
          >
            {isPC && (
              <Box
                className="w-1/5 h-full bg-gray"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <SuspenseWrapper
                  loading={loadingMotto}
                  skeletonProps={{ variant: "rounded", width: "20%", height: "200px", animation: "wave" }}
                >
                  <CardMedia
                    component="img"
                    image={
                      "https://firebasestorage.googleapis.com/v0/b/bsb-project-99087.appspot.com/o/images%2Flogos%2FLogoWithNames.jpg?alt=media&token=7d8f0f73-90b8-47f5-bc5a-724589b27512"
                    }
                    alt="logoWithName"
                    sx={{ objectFit: "cover", height: "100%", width: "100%" }}
                  />
                </SuspenseWrapper>
              </Box>
            )}

            <Box className={`flex flex-col ${isMobile ? "w-full" : "w-4/5"} items-center justify-center`} sx={{ width: isMobile ? "100%" : "75%" }}>
              <SuspenseWrapper
                loading={loadingMotto}
                skeletonProps={{ variant: "rounded", width: "100%", height: "300px", animation: "wave" }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      m: 2,
                      width: isMobile ? "100%" : "auto",
                      background: "#f5f5f5",
                    }}
                  >
                    {/* ⚠️ Le contenu est HTML — on part du principe qu’il vient d’une source de confiance */}
                    <Typography
                      variant="h6"
                      textAlign="justify"
                      sx={{ color: "text.primary" }}
                      dangerouslySetInnerHTML={{ __html: motto.motto }}
                    />
                  </Paper>
                </Box>
              </SuspenseWrapper>
            </Box>
          </motion.div>
        ))}
      </motion.div>
    </Box>
  );
};

/** Utils */
function isPlainObject(v: any): v is Record<string, any> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}
function stripHtml(html: string): string {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}
